import { ChangeEvent, useEffect, useState } from 'react'

import { PageTitle } from '@/components'
import { Button, Input, Modal } from '@/components/ui'
import FormGroup from '@/core/components/FormGroup'
import { FormUploadPreview } from '@/core/components/FormUploadPreview'
import { uploadImages } from '@/core/services/upload-image'
import {
  RankTranslation,
  RankUpsert,
  RoadmapRank,
} from '@/common/models/gamification'
import { createRank, updateRank } from '@/core/services/gamification'

import { TranslationTabs } from '../shared/TranslationTabs'

interface RankEditorModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  roadmapId: string
  rank?: RoadmapRank | null
  onChanged: () => Promise<void> | void
  onError: (e: unknown) => void
}

/**
 * Create/edit a roadmap rank: a points threshold + optional image + localized name(s).
 * Mirrors StepEditorModal's plain-state + inline image-upload style.
 */
export const RankEditorModal = ({
  open,
  onOpenChange,
  roadmapId,
  rank,
  onChanged,
  onError,
}: RankEditorModalProps) => {
  const isEditMode = !!rank

  const [pointsThreshold, setPointsThreshold] = useState('')
  const [imageRef, setImageRef] = useState('')
  const [translations, setTranslations] = useState<RankTranslation[]>([])
  const [saving, setSaving] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | undefined>()

  useEffect(() => {
    if (!open) return
    if (rank) {
      setPointsThreshold(String(rank.pointsThreshold))
      setImageRef(rank.imageRef ?? '')
      setTranslations(rank.translations ?? [])
    } else {
      setPointsThreshold('')
      setImageRef('')
      setTranslations([])
    }
    setUploadError(undefined)
    setIsUploading(false)
  }, [open, rank])

  const handleImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 1_000_000) {
      setUploadError('The image is too large (max 1 MB)')
      return
    }
    setUploadError(undefined)
    setIsUploading(true)
    try {
      const { data } = await uploadImages(file, true, 'GAMIFICATION_RANK')
      if (data?.photoUrl) setImageRef(data.photoUrl)
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status
      setUploadError(
        status === 400 ? 'Image upload rejected' : 'Image upload failed',
      )
    } finally {
      setIsUploading(false)
    }
  }

  const thresholdNumber = pointsThreshold === '' ? NaN : Number(pointsThreshold)
  const thresholdValid =
    pointsThreshold !== '' &&
    Number.isFinite(thresholdNumber) &&
    thresholdNumber >= 0
  const invalid = !thresholdValid

  const handleClose = () => onOpenChange(false)

  const submit = async () => {
    if (invalid) return
    const data: RankUpsert = {
      pointsThreshold: thresholdNumber,
      imageRef: imageRef || undefined,
      translations,
    }
    try {
      setSaving(true)
      if (rank) {
        await updateRank(rank.id, data)
      } else {
        await createRank(roadmapId, data)
      }
      await onChanged()
      handleClose()
    } catch (e) {
      onError(e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose()
      }}
      size="lg"
      title={
        <div className="border-b border-monochrome-120 pb-4">
          <PageTitle
            icon="flash"
            title={
              <span className="flex items-center gap-2">
                {isEditMode ? 'Edit' : 'Add New'}{' '}
                <span className="text-accent-1">Rank</span>
              </span>
            }
          />
        </div>
      }
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button onClick={handleClose} type="button" variant="secondary">
            Cancel
          </Button>
          <Button
            onClick={submit}
            type="button"
            variant="primary"
            disabled={invalid || saving || isUploading}
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <FormGroup
          label="Points threshold"
          error={!thresholdValid ? 'Must be a number ≥ 0' : undefined}
        >
          <Input
            isInvalid={!thresholdValid}
            value={pointsThreshold}
            onChange={(e) => setPointsThreshold(e.target.value)}
            type="number"
            min="0"
            placeholder="Enter points threshold"
          />
        </FormGroup>

        <div>
          <FormUploadPreview
            label="Image (optional)"
            previewLabel="Image"
            isUploading={isUploading}
            url={imageRef}
            width={60}
            height={60}
            imageName={imageRef ? 'rank image' : undefined}
            onRemove={() => setImageRef('')}
            imageStyle={{ objectFit: 'contain' }}
            control={
              <Input
                multiple={false}
                accept="image/*"
                isInvalid={!!uploadError}
                type="file"
                className="bg-monochrome-5-50"
                onChange={handleImageUpload}
              />
            }
          />
          {uploadError && (
            <span className="text-xs text-accent-3">{uploadError}</span>
          )}
        </div>

        <TranslationTabs<RankTranslation>
          value={translations}
          onChange={setTranslations}
          makeEmpty={(locale) => ({ locale, name: '' })}
          renderFields={({ value, onFieldChange }) => (
            <FormGroup label="Name">
              <Input
                value={value.name ?? ''}
                onChange={(e) => onFieldChange({ name: e.target.value })}
                type="text"
                placeholder="Enter translated name"
              />
            </FormGroup>
          )}
        />
      </div>
    </Modal>
  )
}
